import React, { useEffect, useRef, useState } from 'react';

import {
  IMPORT_HIERARCHY_PARENT_TYPES,
  searchImportParentCandidates,
} from './lib/import-service';
import type { ImportParentCandidate } from './lib/import-service';
import { Input } from './ui';

export const formatParentCandidate = (
  candidate: Pick<ImportParentCandidate, 'name' | 'sku'>,
) => [candidate.sku, candidate.name].filter(Boolean).join(' · ');

export const ImportParentProductPicker: React.FC<{
  databaseId: string;
  childHierarchyType: string;
  value: ImportParentCandidate | null;
  onChange: (value: ImportParentCandidate | null) => void;
  excludeProductId?: string;
  className?: string;
  compact?: boolean;
}> = ({
  databaseId,
  childHierarchyType,
  value,
  onChange,
  excludeProductId,
  className,
  compact,
}) => {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [candidates, setCandidates] = useState<ImportParentCandidate[]>([]);
  const requestRef = useRef(0);
  const allowedTypes = IMPORT_HIERARCHY_PARENT_TYPES[childHierarchyType];

  useEffect(() => {
    if (!open || !allowedTypes || !databaseId) return;
    const requestId = ++requestRef.current;
    const controller = new AbortController();
    setLoading(true);
    const timeout = window.setTimeout(() => {
      searchImportParentCandidates(
        databaseId,
        childHierarchyType,
        query,
        controller.signal,
      ).then(({ success, candidates: results, error: searchError }) => {
        if (requestId !== requestRef.current) return;
        setLoading(false);
        setError(success ? null : searchError ?? 'Failed to load parents');
        setCandidates(
          success
            ? results.filter((candidate) => candidate.id !== excludeProductId)
            : [],
        );
      });
    }, 250);
    return () => {
      window.clearTimeout(timeout);
      controller.abort();
    };
  }, [
    allowedTypes,
    childHierarchyType,
    databaseId,
    excludeProductId,
    open,
    query,
  ]);

  if (!allowedTypes) {
    return (
      <div className='ih-add-product-field-help'>
        {childHierarchyType === 'Assembly'
          ? 'Assemblies are top-level products and have no parent.'
          : 'Choose Sub-Assembly or Component to assign a parent.'}
      </div>
    );
  }

  return (
    <div className='ih-hts-picker'>
      <Input
        value={open ? query : value ? formatParentCandidate(value) : ''}
        role='combobox'
        aria-expanded={open && candidates.length > 0}
        onFocus={() => {
          setQuery('');
          setOpen(true);
        }}
        onBlur={() => window.setTimeout(() => setOpen(false), 120)}
        onChange={(event) => {
          setQuery(event.target.value);
          setOpen(true);
        }}
        placeholder={`Search ${allowedTypes.join(' or ')} by SKU or name`}
        className={className}
        style={compact ? { height: 28, fontSize: 12 } : undefined}
      />
      {open && loading && <span className='ih-hts-loading'>Searching…</span>}
      {open && !loading && (
        <div className='ih-hts-options' role='listbox'>
          {value && (
            <button
              type='button'
              role='option'
              aria-selected={false}
              className='ih-hts-option'
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                onChange(null);
                setOpen(false);
              }}
            >
              <span>No parent</span>
              <span>Remove the current parent link</span>
            </button>
          )}
          {error ? (
            <div className='ih-hts-option'>{error}</div>
          ) : candidates.length === 0 ? (
            <div className='ih-hts-option'>
              No matching {allowedTypes.join(' or ')} products
            </div>
          ) : (
            candidates.map((candidate) => (
              <button
                type='button'
                role='option'
                aria-selected={candidate.id === value?.id}
                key={candidate.id}
                className='ih-hts-option'
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  onChange(candidate);
                  setOpen(false);
                }}
              >
                <span className='ih-mono'>{candidate.sku || '—'}</span>
                <span>
                  {candidate.name} · {candidate.hierarchyType}
                </span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
};
